import { Autocomplete, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Tab, Tabs, TextField } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { DataTable, DateText, ErrorState, PageHeader, PermissionGate, StatusChip, useListParams } from "../components";
import { errorMessage } from "../api/client";
import help from "./HELP.md?raw";
import { useInvitationAction, useInvitations, useInvite, useMembers, useRemoveMember, useRoleOptions, useSiteOptions, useUpdateMember, type Invitation, type Member } from "./useData";

function InviteDialog({ onClose }: { onClose: () => void }) {
  const roles = useRoleOptions();
  const sites = useSiteOptions();
  const invite = useInvite();
  const { me } = useAuth();
  const [form, setForm] = useState({ email: "", roles: [] as string[], sites: [] as string[], is_owner: false, message: "" });
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Invite a teammate</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
        {error && <ErrorState message={error} />}
        <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus required sx={{ mt: 1 }} />
        <Autocomplete multiple options={roles.data ?? []} getOptionLabel={(r) => r.name} value={(roles.data ?? []).filter((r) => form.roles.includes(r.id))}
          onChange={(_, v) => setForm({ ...form, roles: v.map((r) => r.id) })} renderInput={(p) => <TextField {...p} label="Roles" />} />
        <Autocomplete multiple options={sites.data ?? []} getOptionLabel={(s) => `${s.code} ${s.name}`} value={(sites.data ?? []).filter((s) => form.sites.includes(s.id))}
          onChange={(_, v) => setForm({ ...form, sites: v.map((s) => s.id) })} renderInput={(p) => <TextField {...p} label="Restrict to sites (empty = all)" />} />
        {me?.active_organization?.is_owner && <FormControlLabel control={<Checkbox checked={form.is_owner} onChange={(e) => setForm({ ...form, is_owner: e.target.checked })} />} label="Make this person an owner" />}
        <TextField label="Personal message" multiline minRows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.email || invite.isPending} onClick={async () => {
          try { await invite.mutateAsync(form); onClose(); } catch (err) { setError(errorMessage(err)); }
        }}>Send invitation</Button>
      </DialogActions>
    </Dialog>
  );
}

function EditMemberDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const roles = useRoleOptions();
  const sites = useSiteOptions();
  const update = useUpdateMember();
  const { me } = useAuth();
  const [form, setForm] = useState({ roles: member.roles, sites: member.sites, title: member.title, status: member.status, is_owner: member.is_owner });
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>{member.full_name || member.email}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
        {error && <ErrorState message={error} />}
        <TextField label="Job title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mt: 1 }} />
        <Autocomplete multiple options={roles.data ?? []} getOptionLabel={(r) => r.name} value={(roles.data ?? []).filter((r) => form.roles.includes(r.id))}
          onChange={(_, v) => setForm({ ...form, roles: v.map((r) => r.id) })} renderInput={(p) => <TextField {...p} label="Roles" />} />
        <Autocomplete multiple options={sites.data ?? []} getOptionLabel={(s) => `${s.code} ${s.name}`} value={(sites.data ?? []).filter((s) => form.sites.includes(s.id))}
          onChange={(_, v) => setForm({ ...form, sites: v.map((s) => s.id) })} renderInput={(p) => <TextField {...p} label="Restrict to sites (empty = all)" />} />
        <TextField select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} slotProps={{ select: { native: true } }} disabled={member.user_id === me?.id}>
          <option value="active">Active</option><option value="disabled">Disabled</option>
        </TextField>
        {me?.active_organization?.is_owner && <FormControlLabel control={<Checkbox checked={form.is_owner} onChange={(e) => setForm({ ...form, is_owner: e.target.checked })} />} label="Owner (full access, billing, can close the organization)" />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={update.isPending} onClick={async () => {
          try { await update.mutateAsync({ id: member.id, ...form }); onClose(); } catch (err) { setError(errorMessage(err)); }
        }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

export function Members() {
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState<"members" | "invitations">("members");
  const [params, setParams] = useListParams({ ordering: "user__email" });
  const [invParams, setInvParams] = useListParams({ status: "pending" });
  const members = useMembers(params);
  const invitations = useInvitations(invParams);
  const remove = useRemoveMember();
  const invAction = useInvitationAction();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);

  const memberColumns: GridColDef<Member>[] = [
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    { field: "full_name", headerName: "Name", width: 180 },
    { field: "title", headerName: "Title", width: 140 },
    { field: "role_names", headerName: "Roles", flex: 1, sortable: false, renderCell: (p) => <Stack direction="row" spacing={0.5} sx={{ py: 0.5 }}>{p.row.is_owner && <Chip label="Owner" size="small" color="primary" />}{(p.value as string[]).map((r) => <Chip key={r} label={r} size="small" />)}</Stack> },
    { field: "status", headerName: "Status", width: 110, renderCell: (p) => <StatusChip status={p.value as string} /> },
    { field: "last_login", headerName: "Last sign-in", width: 140, renderCell: (p) => <DateText value={p.value as string} withTime /> },
    { field: "actions", headerName: "", width: 140, sortable: false, renderCell: (p) => hasPermission("accounts.membership.change") ? (
      <><Button size="small" onClick={() => setEditing(p.row)}>Edit</Button>
        <PermissionGate permission="accounts.membership.remove"><Button size="small" color="error" onClick={() => { if (confirm(`Remove ${p.row.email}?`)) remove.mutate(p.row.id); }}>Remove</Button></PermissionGate></>
    ) : null },
  ];
  const invColumns: GridColDef<Invitation>[] = [
    { field: "email", headerName: "Email", flex: 1 },
    { field: "status", headerName: "Status", width: 110, renderCell: (p) => <StatusChip status={p.value as string} /> },
    { field: "invited_by_name", headerName: "Invited by", width: 160 },
    { field: "expires_at", headerName: "Expires", width: 140, renderCell: (p) => <DateText value={p.value as string} /> },
    { field: "actions", headerName: "", width: 160, sortable: false, renderCell: (p) => p.row.status === "pending" || p.row.status === "expired" ? (
      <><PermissionGate permission="accounts.membership.invite"><Button size="small" onClick={() => invAction.mutate({ id: p.row.id, action: "resend" })}>Resend</Button></PermissionGate>
        <PermissionGate permission="accounts.invitation.delete"><Button size="small" color="error" onClick={() => invAction.mutate({ id: p.row.id, action: "revoke" })}>Revoke</Button></PermissionGate></>
    ) : null },
  ];

  return (
    <>
      <PageHeader help={help} title="Members" crumbs={[{ label: "Settings" }, { label: "Members" }]}
        actions={<PermissionGate permission="accounts.membership.invite"><Button variant="contained" onClick={() => setInviting(true)}>Invite</Button></PermissionGate>} />
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="members" label={`Members${members.data ? ` (${members.data.count})` : ""}`} />
        <Tab value="invitations" label={`Invitations${invitations.data ? ` (${invitations.data.count})` : ""}`} />
      </Tabs>
      {(remove.isError || invAction.isError) && <ErrorState message={(remove.error ?? invAction.error)?.message ?? ""} />}
      {tab === "members" ? (
        <DataTable viewKey="accounts.membership.list" columns={memberColumns} data={members.data} isLoading={members.isLoading} params={params} onParamsChange={setParams} exportPath="/api/v1/accounts/members/export/" />
      ) : (
        <DataTable viewKey="accounts.invitation.list" columns={invColumns} data={invitations.data} isLoading={invitations.isLoading} params={invParams} onParamsChange={setInvParams}
          filters={<TextField select size="small" label="Status" value={String(invParams.status ?? "")} onChange={(e) => setInvParams({ ...invParams, status: e.target.value, page: 1 })} slotProps={{ select: { native: true } }} sx={{ width: 160 }}>
            <option value="">All</option><option value="pending">Pending</option><option value="accepted">Accepted</option><option value="expired">Expired</option><option value="revoked">Revoked</option>
          </TextField>} />
      )}
      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
      {editing && <EditMemberDialog member={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
